import type {
  AssetMetaResp,
  AuthResp,
  ProjectManifest,
  ProjectResp,
  UserResp,
} from "@pix/shared";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.pixadv.net";

/** Portal と JWT を共有する(同一キー)。旧キー pix-auth-token も互換のため読む。 */
const TOKEN_KEY = "pix-studio:token";
const LEGACY_TOKEN_KEY = "pix-auth-token";
let authToken: string | null = null;

export function getToken(): string | null {
  if (authToken === null) {
    try {
      authToken =
        localStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(LEGACY_TOKEN_KEY);
      // 旧キーで読めた場合は新キーへマイグレート
      if (authToken && !localStorage.getItem(TOKEN_KEY)) {
        localStorage.setItem(TOKEN_KEY, authToken);
        localStorage.removeItem(LEGACY_TOKEN_KEY);
      }
    } catch {
      /* ignore */
    }
  }
  return authToken;
}

export function setToken(token: string | null): void {
  authToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    }
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (
    !headers.has("Content-Type") &&
    options.body !== undefined &&
    options.body !== null &&
    typeof options.body === "string"
  ) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(API_BASE + path, { ...options, headers });
  if (res.status === 401) {
    setToken(null);
    throw new ApiError(401, "認証が切れました。ログインし直してください");
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, data.error ?? res.statusText);
  }
  return res;
}

export const api = {
  // ----- Auth
  async signup(email: string, password: string, displayName?: string): Promise<UserResp> {
    const res = await apiFetch("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName }),
    });
    const data = (await res.json()) as AuthResp;
    setToken(data.token);
    return data.user;
  },

  async login(email: string, password: string): Promise<UserResp> {
    const res = await apiFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    const data = (await res.json()) as AuthResp;
    setToken(data.token);
    return data.user;
  },

  async me(): Promise<UserResp | null> {
    if (!getToken()) return null;
    try {
      const res = await apiFetch("/api/auth/me");
      return (await res.json()) as UserResp;
    } catch {
      return null;
    }
  },

  logout(): void {
    setToken(null);
  },

  // ----- Projects
  async listProjects(): Promise<ProjectResp[]> {
    const res = await apiFetch("/api/projects");
    return (await res.json()) as ProjectResp[];
  },

  async createProject(
    name: string,
    manifest?: ProjectManifest,
    scripts?: Record<string, string>,
  ): Promise<ProjectResp> {
    const res = await apiFetch("/api/projects", {
      method: "POST",
      body: JSON.stringify({ name, manifest, scripts }),
    });
    return (await res.json()) as ProjectResp;
  },

  async getProject(id: string): Promise<ProjectResp> {
    const res = await apiFetch(`/api/projects/${encodeURIComponent(id)}`);
    return (await res.json()) as ProjectResp;
  },

  async updateProject(
    id: string,
    patch: { name?: string; manifest?: ProjectManifest; scripts?: Record<string, string> },
  ): Promise<ProjectResp> {
    const res = await apiFetch(`/api/projects/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return (await res.json()) as ProjectResp;
  },

  async deleteProject(id: string): Promise<void> {
    await apiFetch(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // ----- Assets
  async listAssets(projectId: string): Promise<AssetMetaResp[]> {
    const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/assets`);
    return (await res.json()) as AssetMetaResp[];
  },

  async uploadAsset(projectId: string, path: string, blob: Blob): Promise<AssetMetaResp> {
    const headers = new Headers();
    if (blob.type) headers.set("Content-Type", blob.type);
    const res = await apiFetch(
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURI(path)}`,
      {
        method: "PUT",
        body: blob,
        headers,
      },
    );
    return (await res.json()) as AssetMetaResp;
  },

  async downloadAsset(projectId: string, path: string): Promise<Blob> {
    const res = await apiFetch(
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURI(path)}`,
    );
    return await res.blob();
  },

  async deleteAsset(projectId: string, path: string): Promise<void> {
    await apiFetch(
      `/api/projects/${encodeURIComponent(projectId)}/assets/${encodeURI(path)}`,
      { method: "DELETE" },
    );
  },

  // ----- Portal publish
  async publishWork(input: PublishInput): Promise<PublishResult> {
    const res = await apiFetch("/api/portal/works", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return (await res.json()) as PublishResult;
  },

  async unpublishWork(workId: string): Promise<void> {
    await apiFetch(`/api/portal/works/${encodeURIComponent(workId)}`, {
      method: "DELETE",
    });
  },

  /** 既に公開済みかを確認(404 なら未公開) */
  async fetchPublishedWork(workId: string): Promise<PublishedWork | null> {
    try {
      const res = await apiFetch(`/api/portal/works/${encodeURIComponent(workId)}`);
      const data = (await res.json()) as { work: PublishedWork };
      return data.work;
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
};

// =============================================================
// Portal Publish 型
// =============================================================

export interface PublishInput {
  projectId: string;
  title: string;
  shortDescription?: string;
  description?: string;
  genre?: string;
  ageRating?: string;
  price?: number;
  estimatedPlayTime?: number;
  tags?: string[];
  isPublished?: boolean;
  thumbnail?: string | null;
  screenshots?: string[];
}

export interface PublishedWork {
  id: string;
  title: string;
  shortDescription: string;
  description?: string;
  genre: string;
  ageRating: string;
  price: number;
  estimatedPlayTime?: number;
  tags: string[];
  isPublished: boolean;
  isDraft: boolean;
  isPrivate: boolean;
  publishedAt: number | null;
  updatedAt: number;
  thumbnail?: string;
  screenshots?: string[];
}

export interface PublishResult {
  work: PublishedWork | null;
}
